import React from 'react';
import ReactDOM from 'react-dom';
import { PDFExportService } from './PDFExportService';
import PlanPdfCurrentView from '../components/PlanPdfCurrentView';

const ensureTailwindForExport = async () => {
  // If tailwind is already present, do nothing.
  const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find((l) =>
    (l.getAttribute('href') || '').includes('application.tailwind')
  );
  if (existing) return { added: false };

  // Try the digested asset if it exists on the page as a preload/meta (future-proof),
  // otherwise fall back to the logical sprockets path.
  const hintedHref =
    document.querySelector('meta[name="application-tailwind-href"]')?.getAttribute('content') ||
    '/assets/application.tailwind.css';

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = hintedHref;
  document.head.appendChild(link);

  await new Promise((resolve) => {
    const timer = setTimeout(resolve, 1500); // best-effort; don't hang export forever
    link.addEventListener('load', () => { clearTimeout(timer); resolve(); }, { once: true });
    link.addEventListener('error', () => { clearTimeout(timer); resolve(); }, { once: true });
  });

  return { added: true, link };
};

/**
 * Render a plan document off-screen and export it to PDF.
 * This is used by admin background downloads (no impersonation) and can be reused elsewhere.
 */
export const exportProgressDocumentToPdf = async ({
  content,
  userName = 'User',
  companyName = 'Practice',
  filename = 'plan.pdf',
}) => {
  if (!content) {
    throw new Error('Missing content for PDF export');
  }

  const elementId = `plan-document-preview-${Date.now()}`;

  const tailwind = await ensureTailwindForExport();

  const mount = document.createElement('div');
  mount.style.position = 'fixed';
  mount.style.left = '-10000px';
  mount.style.top = '0';
  mount.style.width = '900px';
  mount.style.background = '#ffffff';
  document.body.appendChild(mount);

  ReactDOM.render(
    <div id={elementId} data-plan-markdown-export-root="true">
      <PlanPdfCurrentView content={content} />
    </div>,
    mount
  );

  // Ensure DOM has painted before html2pdf snapshots it.
  await new Promise((resolve) => setTimeout(resolve, 50));

  const element = document.getElementById(elementId) || mount.querySelector('[data-plan-markdown-export-root="true"]');
  if (!element) {
    ReactDOM.unmountComponentAtNode(mount);
    document.body.removeChild(mount);
    throw new Error('Rendered document root not found for PDF export');
  }

  const result = await PDFExportService.exportHtmlToPdf(element, filename);

  ReactDOM.unmountComponentAtNode(mount);
  document.body.removeChild(mount);
  if (tailwind.added && tailwind.link?.parentNode) {
    tailwind.link.parentNode.removeChild(tailwind.link);
  }

  if (!result?.success) {
    throw new Error(result?.error || 'PDF export failed');
  }

  return result;
};


